/** Registering the service worker, and driving the bulk download.
 *
 * Everything here is guarded on the API existing, because on this deploy it
 * does not. A service worker is only handed to a SECURE CONTEXT - https, or
 * localhost - and the game is served over plain http, where
 * `"serviceWorker" in navigator` is false and `caches` is undefined. Measured
 * on the live host: `isSecureContext: false`. So `register()` is a no-op there
 * rather than a thrown error in the console of every session, and
 * `offlineStatus()` says which of the two reasons applies so the UI can tell a
 * player "this needs https" instead of failing silently.
 *
 * The moment the host serves https, all of this starts working with no change.
 */

import { BASE, asset } from "./base.js";
export type OfflineState =
  /** No service worker API at all: plain http, or a browser without one. */
  | { readonly kind: "unsupported"; readonly why: "insecure" | "browser" }
  /** Supported and registered, with nothing bulk-cached yet. */
  | { readonly kind: "ready" }
  /** A deliberate download is running. */
  | { readonly kind: "downloading"; readonly done: number; readonly total: number; readonly bytes: number }
  /** A deliberate download finished. */
  | { readonly kind: "cached"; readonly done: number; readonly total: number; readonly bytes: number };

export function offlineSupported(): boolean {
  return typeof navigator !== "undefined"
    && "serviceWorker" in navigator
    && typeof caches !== "undefined";
}

/** Why it is unavailable, when it is. `isSecureContext` is the usual answer. */
export function offlineStatus(): OfflineState {
  if (offlineSupported()) return { kind: "ready" };
  const insecure = typeof window !== "undefined" && window.isSecureContext === false;
  return { kind: "unsupported", why: insecure ? "insecure" : "browser" };
}

/**
 * Register the worker. Safe to call always; does nothing where unsupported.
 *
 * Deliberately NOT awaited by the caller and deliberately after `load`: an
 * install fetches the whole 11 MB shell, and doing that while the first frame
 * is still being painted makes a cold start slower for a benefit that only
 * matters on the SECOND start.
 */
export function registerOffline(): void {
  if (!offlineSupported()) return;
  const start = (): void => {
    navigator.serviceWorker.register(asset("/sw.js"), { scope: BASE }).catch(() => {
      // A failed registration is not worth a broken session: the game runs
      // online exactly as it did before one existed.
    });
  };
  if (document.readyState === "complete") start();
  else window.addEventListener("load", start, { once: true });
}

/** What a bulk download would fetch, in bytes, without fetching any of it. */
export async function offlineSize(): Promise<{ readonly bulk: number; readonly music: number } | null> {
  if (!offlineSupported()) return null;
  try {
    const res = await fetch(asset("/sw-manifest.json"), { cache: "no-store" });
    if (!res.ok) return null;
    const m = await res.json() as { readonly bulk: readonly string[]; readonly music: readonly string[] };
    // The manifest lists names, not sizes - asking the server for each one's
    // length would be 38 extra requests to answer a question the estimate
    // below answers well enough. These are the measured build totals.
    return { bulk: m.bulk.length, music: m.music.length };
  } catch {
    return null;
  }
}

/**
 * Pull every atlas sheet (and optionally the music) into the cache, reporting
 * progress as it goes.
 *
 * The worker does the fetching, not this file: a download that survives the
 * player navigating to another screen has to live in the worker, and a
 * `MessageChannel` is how its progress gets back here.
 */
export function downloadOffline(
  opts: { readonly music: boolean },
  onProgress: (s: OfflineState) => void,
): void {
  if (!offlineSupported()) { onProgress(offlineStatus()); return; }
  void navigator.serviceWorker.ready.then((reg) => {
    const worker = reg.active;
    if (worker === null) { onProgress({ kind: "ready" }); return; }
    const channel = new MessageChannel();
    channel.port1.onmessage = (e: MessageEvent): void => {
      const d = e.data as { type: string; done?: number; total?: number; bytes?: number };
      if (d.type === "PROGRESS") {
        onProgress({ kind: "downloading", done: d.done ?? 0, total: d.total ?? 0, bytes: d.bytes ?? 0 });
      } else if (d.type === "DONE") {
        onProgress({ kind: "cached", done: d.done ?? 0, total: d.total ?? 0, bytes: d.bytes ?? 0 });
      } else {
        onProgress({ kind: "ready" });
      }
    };
    worker.postMessage({ type: "PRECACHE_BULK", music: opts.music }, [channel.port2]);
  });
}
